<template>
  <div :class="['beta', 'dev'].includes(kind) ? 'tw-pt-2' : ''">
    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
      <div class="tw-space-y-2">
        <Text>
          {{ t('sidebar_status.version') }}
          <Text tag="span" class="tw-font-black">
            {{ displayedVersion }}
          </Text>
        </Text>
      </div>

      <Tag v-if="state === 'available_version'" color="orange" size="small" class="tw-flex-shrink-0">
        {{ t('sidebar_status.tag_update') }}
      </Tag>

      <Tag v-else-if="state === 'download_in_progress'" size="small" color="orange" class="tw-flex-shrink-0">
        {{ t('sidebar_status.tag_downloading') }}
      </Tag>

      <Tag v-else-if="state === 'download_finished'" size="small" color="green" class="tw-flex-shrink-0">
        {{ t('sidebar_status.tag_ready') }}
      </Tag>

      <Tag v-else-if="state === 'download_not_finished'" size="small" color="red" class="tw-flex-shrink-0">
        {{ t('sidebar_status.tag_failed') }}
      </Tag>
    </div>

    <div class="tw-space-y-2">
      <div v-if="state === 'available_version'" class="tw-space-y-2">
        <template v-if="hasSize">
          <Text>
            {{ t('sidebar_status.size') }}
            <Text tag="span" class="tw-font-black">{{ formatBytes(remote?.size_bytes) }}</Text>
          </Text>

          <Text v-if="estimatedMinutes !== null" class="tw-opacity-80">
            {{ t('sidebar_status.estimated_download_time') }}
            <Text tag="span" class="tw-font-black">
              {{ t('sidebar_status.minutes', { count: estimatedMinutes }) }}
            </Text>
          </Text>
        </template>

        <Text>
          {{ t('sidebar_status.click') }}
          <Link href="#" scheme="underline" @click.prevent="onDownload">{{ t('sidebar_status.here') }}</Link>
          {{ t('sidebar_status.to_download_it') }}
        </Text>
      </div>

      <div v-else-if="state === 'download_in_progress'" class="tw-mt-2 tw-space-y-2">
        <template v-if="hasSize">
          <ProgressBar
            :percentage="progress"
            :bg-attrs="{ class: 'tw-bg-white tw-bg-opacity-7' }"
            :bar-attrs="{ class: 'tw-bg-primary' }"
          />
        </template>

        <Text size="small">
          {{ t('sidebar_status.remaining_time') }}
          {{ remainingTimeText }}
        </Text>

        <Link href="#" scheme="underline" textSize="small" class="tw-inline-block" @click.prevent="openRunnerLogs">
          {{ t('sidebar_status.open_runner_logs') }}
        </Link>
      </div>

      <div v-else-if="state === 'download_finished' && showRun" class="tw-space-y-2">
        <Text>
          {{ t('sidebar_status.click') }}
          <Link href="#" scheme="underline" @click.prevent="onRun">{{ t('sidebar_status.here') }}</Link>
          {{ t('sidebar_status.to_run_downloaded') }}
        </Text>
      </div>

      <div v-else-if="state === 'download_not_finished'" class="tw-space-y-2">
        <Text>
          {{ t('sidebar_status.click') }}
          <Link href="#" scheme="underline" @click.prevent="onDownload">{{ t('sidebar_status.here') }}</Link>
          {{ t('sidebar_status.to_retry_download') }}
        </Text>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Link, ProgressBar, Tag, Text } from '@slideslive/fuse-kit/vue';

import { formatBytes } from '@/utils/sidebar_status';

const props = defineProps({
  kind: { type: String, required: true },
  state: { type: String, required: true },
  displayedVersion: { type: String, required: true },
  remote: { type: Object, default: null },
  estimatedMinutes: { type: [Number, null], default: null },
  progress: { type: Number, default: 0 },
  remainingTimeText: { type: String, default: '-' },
  showRun: { type: Boolean, default: true },
});

const emit = defineEmits(['download', 'run', 'open-logs']);

const { t } = useI18n();

const hasSize = computed(() => Number(props.remote?.size_bytes || 0) > 0);

const onDownload = (event) => {
  emit('download', event);
};

const onRun = (event) => {
  emit('run', event);
};

const openRunnerLogs = () => {
  emit('open-logs', { kind: 'runner' });
};
</script>
